/**
 * Spec 053 Flow E / AC-28, AC-29, AC-30 — nightly proofs contract.
 * Mirrors merchant-settlement `GET /api/admin/finance/proofs`, `GET /proofs/{runId}` and
 * `POST /proofs/run?date=` (S8 design) field-for-field: `ProofRunResponse.java`. One row of
 * `ledger_proof_runs` per (run_date, proof); the table is mutable, so an on-demand run replaces
 * the night's row for that date.
 */
import type { ProofRunStatus } from './overview';

export type { ProofRunStatus };

/** 1 = payout integrity (2150 clears), 2 = custody (Stripe-anchored delta), 3 = VAT classification. */
export type ProofNumber = 1 | 2 | 3;

export const PROOF_NUMBERS: ProofNumber[] = [1, 2, 3];

export const PROOF_TITLES: Record<ProofNumber, string> = {
  1: 'Payout integrity',
  2: 'Custody',
  3: 'VAT classification',
};

/** Strip cell: the night's run for one proof, or null when no run exists for that day. */
export interface ProofDaySummary {
  runId: string;
  status: ProofRunStatus;
  variance: number | null;
}

export interface ProofDay {
  date: string;
  /** Keyed by proof number as a string ("1", "2", "3") — JSON object keys are strings. */
  proofs: Record<string, ProofDaySummary | null>;
}

export interface ProofsListResponse {
  ledgerLiveSince: string;
  /** Newest first or oldest first is the backend's choice; the strip sorts by date itself. */
  days: ProofDay[];
}

/** One exception row: what an admin can tick off (a settlement, a journal, a payout, a document). */
export interface ProofException {
  /** TERMS_BREACH, CLEARING_MISMATCH, PRE_LEDGER_PAID, SETTLEMENT_PAID, REFUND_PAID, OWN_FUNDS_WITHDRAWN, PAYOUT, INV, CCN, … */
  type: string;
  /** Settlement number, journal id, payout id, document number. */
  ref: string | null;
  amount: number | null;
  detail: string | null;
}

/** `lhs` / `rhs` are the proof's stored working: flat or nested numbers, counts and stamps. */
export type ProofSide = Record<string, unknown>;

export interface ProofRunResponse {
  runId: string;
  proof: ProofNumber;
  runDate: string;
  status: ProofRunStatus;
  ranAt: string;
  lhs: ProofSide | null;
  rhs: ProofSide | null;
  variance: number | null;
  exceptions: ProofException[];
  /** Why the proof could not conclude (billing down, no snapshot) — INCONCLUSIVE only. */
  reason?: string | null;
}

/** Days of history the strip shows (Flow E step 3: "the last 31 days"). */
export const PROOF_STRIP_DAYS = 31;
